/** Wraps Urql.useMutation to set the Assistant-Widget-ID header and re-exports any other needed bits of Urql for use by generated GQL hooks. */
import * as Urql from "urql";
export type { UseQueryArgs } from "urql";
import { useCallback, useContext } from "react";

import { WidgetContext, WidgetContextValue } from "../contexts/widget/WidgetContext";

const WIDGET_ID_KEY = "Assistant-Widget-ID";

export const useMutation: typeof Urql.useMutation = (query) => {
    const widgetContext = useContext(WidgetContext);

    const [state, invoke] = Urql.useMutation(query);
    const wrappedInvoke: typeof invoke = useCallback(
        (variables, context) => invoke(variables, updateContext(context, widgetContext)),
        [invoke, widgetContext],
    );

    return [state, widgetContext == null ? invoke : wrappedInvoke];
};

function setWidgetHeader(headers: HeadersInit | undefined, headerValue: string): HeadersInit {
    if (headers == null) {
        return { [WIDGET_ID_KEY]: headerValue };
    }

    if (headers instanceof Headers) {
        const newHeaders = new Headers(headers);
        newHeaders.set(WIDGET_ID_KEY, headerValue);
        return newHeaders;
    }

    if (Array.isArray(headers)) {
        return [...headers, [WIDGET_ID_KEY, headerValue]];
    }

    return { ...headers, [WIDGET_ID_KEY]: headerValue };
}

function updateContext(
    context: Partial<Urql.OperationContext> | undefined,
    widgetContext: WidgetContextValue,
): Partial<Urql.OperationContext> | undefined {
    if (widgetContext == null) {
        return context;
    }

    const headerValue = JSON.stringify(widgetContext);
    const fetchOptions = context?.fetchOptions;
    if (typeof fetchOptions === "function") {
        return {
            ...context,
            fetchOptions: () => {
                const opts = fetchOptions();
                return { ...opts, headers: setWidgetHeader(opts.headers, headerValue) };
            },
        };
    } else {
        return {
            ...context,
            fetchOptions: {
                ...(fetchOptions ?? {}),
                headers: setWidgetHeader(fetchOptions?.headers, headerValue),
            },
        };
    }
}

export const useQuery = Urql.useQuery;
